import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import toast from 'react-hot-toast';
import { LuminexLockup } from '../components/LuminexLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('pending'); // pending | success | error
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;
    const token = params.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Missing verification token.');
      return;
    }
    (async () => {
      let resp;
      try {
        resp = await fetch(`${API_URL}/api/auth/verify-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
      } catch {
        setStatus('error');
        setMessage('Network error. Please check your connection and try again.');
        return;
      }
      let detail = '';
      let data = null;
      try {
        // Read body as text first (single consumption), then parse — robust against
        // double-read errors and non-JSON error responses.
        const raw = await resp.text();
        try { data = raw ? JSON.parse(raw) : null; } catch { data = { message: raw }; }
        detail = (data && (data.detail || data.message)) || '';
        if (resp.ok) {
          setStatus('success');
          setMessage((data && data.message) || 'Email verified.');
          toast.success('Email verified — please sign in.');
          setTimeout(() => navigate('/login', { replace: true }), 1600);
          return;
        }
      } catch {
        // Body unreadable — fall through to generic message below
      }
      setStatus('error');
      setMessage(detail || 'This verification link is invalid or has already been used.');
    })();
  }, [params]);

  return (
    <div data-testid="verify-email-page" style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-0)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 460, background: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 12, padding: '40px 36px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
          <LuminexLockup iconSize={32} showDevanagari={true} />
        </div>

        {status === 'pending' && (
          <>
            <div className="spinner" style={{ margin: '0 auto 20px' }} />
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8 }}>Verifying your email…</h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)' }}>This only takes a moment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{ display: 'inline-flex', width: 56, height: 56, borderRadius: '50%', background: 'rgba(14,124,107,0.12)', color: '#0E7C6B', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <CheckCircle size={32} weight="duotone" />
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8, letterSpacing: '-0.01em' }}>Email verified</h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 20, lineHeight: 1.6 }}>{message} Redirecting you to sign in…</p>
            <button className="btn btn-primary btn-block" onClick={() => navigate('/login', { replace: true })} data-testid="verify-goto-login-btn">
              Continue to sign in now
            </button>
          </>
        )}

        {status === 'error' && (
          <>
            <div style={{ display: 'inline-flex', width: 56, height: 56, borderRadius: '50%', background: 'rgba(122,31,31,0.12)', color: '#7A1F1F', alignItems: 'center', justifyContent: 'center', marginBottom: 18 }}>
              <XCircle size={32} weight="duotone" />
            </div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 600, color: 'var(--text-1)', marginBottom: 8, letterSpacing: '-0.01em' }}>Verification failed</h2>
            <p style={{ fontSize: 13, color: 'var(--text-2)', marginBottom: 24, lineHeight: 1.6 }} data-testid="verify-error-msg">{message}</p>
            <button className="btn btn-secondary btn-block" onClick={() => navigate('/login')} data-testid="verify-back-login-btn" style={{ marginBottom: 8 }}>
              Back to sign in
            </button>
            <button className="btn btn-ghost btn-block" onClick={() => navigate('/register')} data-testid="verify-goto-register-btn">
              Create a new account
            </button>
          </>
        )}
      </div>
    </div>
  );
}
